import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #randomCatalogController;

  #gridObserver = new MutationObserver(() => this.#syncProductCardImageSizes());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeGrid();
    this.setAttribute('initialized', '');
    this.randomizeCatalog();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGrid();
    this.randomizeCatalog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver.disconnect();
    this.#randomCatalogController?.abort();
    this.#defaultImageSizes = new WeakMap();
  }


  async randomizeCatalog(force = false) {
    if (!this.hasAttribute('data-random-catalog')) {
      this.#randomCatalogController?.abort();
      return;
    }
    const { grid } = this.refs;
    const marker = this.querySelector('[data-random-catalog-next]');
    if (!grid || !marker || (marker.dataset.ready && !force)) return;
    this.#randomCatalogController?.abort();
    marker.dataset.ready = 'true';
    const controller = new AbortController();
    this.#randomCatalogController = controller;
    grid.setAttribute('aria-busy', 'true');
    const cards = new Map();
    if (marker.dataset.page === '1') {
      for (const card of grid.querySelectorAll(':scope > [data-product-id]')) {
        cards.set(card.dataset.productId, card);
      }
    }
    let nextUrl = marker.dataset.nextUrl;
    if (marker.dataset.page !== '1') {
      const firstUrl = new URL(location.href);
      firstUrl.searchParams.set('page', '1');
      nextUrl = firstUrl.href;
    }
    const visited = new Set();
    try {
      while (nextUrl) {
        const url = new URL(nextUrl, location.origin);
        if (url.origin !== location.origin || visited.has(url.href)) throw new Error('Invalid catalog pagination');
        visited.add(url.href);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to load the complete catalog');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextList = html.querySelector('results-list[data-random-catalog]');
        const nextMarker = nextList?.querySelector('[data-random-catalog-next]');
        if (!nextList || !nextMarker) throw new Error('Missing catalog page');
        for (const card of nextList.querySelectorAll('[ref="grid"] > [data-product-id]')) {
          cards.set(card.dataset.productId, card);
        }
        nextUrl = nextMarker.dataset.nextUrl;
      }
      if (controller.signal.aborted || !this.isConnected || this.refs.grid !== grid) return;
      const shuffled = [...cards.values()];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      for (const [index, card] of shuffled.entries()) {
        card.dataset.page = '1';
        for (const name of [...card.classList]) {
          if (/^product-grid__item--[0-9]+$/.test(name)) card.classList.remove(name);
        }
        card.classList.add('product-grid__item--' + index);
      }
      grid.replaceChildren(...shuffled);
      this.#syncProductCardImageSizes();
    } catch (error) {
      if (!controller.signal.aborted) {
        delete marker.dataset.ready;
        console.warn('Catalog shuffle could not finish.', error);
      }
    } finally {
      if (this.refs.grid === grid && this.#randomCatalogController === controller) grid.removeAttribute('aria-busy');
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncProductCardImageSizes();
  }

  #observeGrid() {
    this.#gridObserver.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
    this.#syncProductCardImageSizes();
  }

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const useSingleColumnSize = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    for (const image of images) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);

      const sizes = useSingleColumnSize ? '100vw' : this.#defaultImageSizes.get(image);
      if (sizes !== undefined && image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

window.addEventListener('pageshow', event => {
  if (!event.persisted) return;
  for (const list of document.querySelectorAll('results-list[data-random-catalog]')) {
    list.randomizeCatalog(true);
  }
});
